// Hosted-editor-only mapping between FlipIntake's in-memory `state` (blob
// URLs, dataURLs — same shape the offline Studio uses) and what actually
// gets persisted to `projects.manifest` (B2 keys) + uploaded to storage.
// Deliberately kept OUT of engine/intake.js — intake.js stays a pure,
// storage-agnostic pipeline shared by both the offline Studio and this app;
// this file is the "how the hosted app persists that state" concern only.
import { uploadAsset } from './api.js'

function pad(n) { return String(n).padStart(3, '0') }

function dataURLtoBlob(dataUrl) {
  const [header, b64] = dataUrl.split(',')
  const mime = header.match(/:(.*?);/)[1]
  const bin = atob(b64)
  const arr = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i)
  return new Blob([arr], { type: mime })
}

/** Uploads every not-yet-uploaded asset for this project. Mutates `state` in
 *  place (sets remoteKey/remoteThumbKey per page, logoKey/bgImageKey/
 *  audioKey/sourceKey), so re-calling after a partial failure only re-sends
 *  what's still missing. */
export async function uploadAllAssets(state, projectId, onProgress) {
  const units = []
  state.pages.forEach((p, i) => {
    if (!p.remoteKey) units.push({ kind: 'page', i })
    if (!p.remoteThumbKey) units.push({ kind: 'thumb', i })
  })
  if (state.logo && !state.logoKey) units.push({ kind: 'logo' })
  if (state.bgImage && !state.bgImageKey) units.push({ kind: 'bg' })
  if (state.audio && !state.audioKey) units.push({ kind: 'audio' })
  if (state.sourceFile && !state.sourceKey) units.push({ kind: 'source' })

  let done = 0
  const total = Math.max(units.length, 1)
  const tick = () => onProgress?.(++done / total)

  for (const u of units) {
    if (u.kind === 'page') {
      const p = state.pages[u.i]
      const key = `${projectId}/pages/p${pad(u.i + 1)}.jpg`
      await uploadAsset(projectId, key, p.blob)
      p.remoteKey = key
    } else if (u.kind === 'thumb') {
      const p = state.pages[u.i]
      const key = `${projectId}/thumbs/t${pad(u.i + 1)}.jpg`
      await uploadAsset(projectId, key, p.thumbBlob)
      p.remoteThumbKey = key
    } else if (u.kind === 'logo') {
      const key = `${projectId}/logo.jpg`
      await uploadAsset(projectId, key, dataURLtoBlob(state.logo))
      state.logoKey = key
    } else if (u.kind === 'bg') {
      const key = `${projectId}/bg.jpg`
      await uploadAsset(projectId, key, dataURLtoBlob(state.bgImage))
      state.bgImageKey = key
    } else if (u.kind === 'audio') {
      const key = `${projectId}/audio${state.audio.ext}`
      await uploadAsset(projectId, key, dataURLtoBlob(state.audio.dataUrl))
      state.audioKey = key
    } else if (u.kind === 'source') {
      const key = `${projectId}/source.pdf`
      await uploadAsset(projectId, key, state.sourceFile)
      state.sourceKey = key
    }
    tick()
  }
}

/** Builds the jsonb blob saved to projects.manifest — same shape the engine
 *  reads (asset fields are B2 keys, not signed URLs; the editor re-signs at
 *  render time), plus a small `_editor` bag so re-opening a draft restores
 *  the wizard's own knobs (accent/bgmode/quality) rather than just the
 *  already-computed theme. */
export function buildPersistManifest(FlipIntake, state) {
  const base = FlipIntake.buildManifest(state, false)
  return {
    ...base,
    logo: state.logoKey || null,
    bgImage: state.bgImageKey || null,
    audio: state.audioKey || null,
    source: state.sourceKey || null,
    pages: state.pages.map((p, i) => ({
      img: p.remoteKey || base.pages[i].img,
      thumb: p.remoteThumbKey || base.pages[i].thumb,
      w: p.w, h: p.h, text: p.text,
      links: base.pages[i].links
    })),
    _editor: { accent: state.accent, bgmode: state.bgmode, quality: state.quality, sourceKind: state.sourceKind, palette: state.palette }
  }
}

/** Restores state from a persisted manifest + a key->signedUrl map (fetched
 *  via get-signed-url) so the editor can re-render a previously-saved draft. */
export function hydrateStateFromManifest(state, manifest, signedUrls) {
  const ed = manifest._editor || {}
  state.title = manifest.title || ''
  state.showCover = manifest.showCover ?? true
  state.flipSoundStyle = manifest.flipSoundStyle || 'soft'
  state.accent = ed.accent || '#4F8EF7'
  state.bgmode = ed.bgmode || 'dark'
  state.quality = ed.quality || 2
  state.sourceKind = ed.sourceKind || null
  state.palette = ed.palette || []
  state.analytics = manifest.analytics || ''
  state.toc = manifest.toc || []
  if (manifest.logo) { state.logoKey = manifest.logo; state.logo = signedUrls[manifest.logo] || null }
  if (manifest.bgImage) { state.bgImageKey = manifest.bgImage; state.bgImage = signedUrls[manifest.bgImage] || null }
  if (manifest.source) state.sourceKey = manifest.source
  // Preserve the audio key so re-saving the draft doesn't drop (and orphan)
  // an already-uploaded music asset. Marker `audio` keeps the picker populated.
  if (manifest.audio) { state.audioKey = manifest.audio; state.audio = { name: 'Background music', ext: (String(manifest.audio).match(/\.[a-z0-9]+$/i) || ['.mp3'])[0] } }
  state.pages = (manifest.pages || []).map((p) => ({
    remoteKey: p.img, remoteThumbKey: p.thumb,
    url: signedUrls[p.img] || p.img, thumbUrl: signedUrls[p.thumb] || p.thumb,
    w: p.w, h: p.h, text: p.text || '', links: p.links || []
  }))
}

/** Collects every B2 KEY on a project's manifest that needs a signed URL to
 *  render (used right after loading an existing draft). A PUBLISHED project's
 *  manifest fields are already permanent https:// CDN URLs, not B2 keys —
 *  those must be skipped here, or get-signed-url rejects them (they don't
 *  match the projectId/ prefix a real key has) and the whole load fails. */
function isDraftKey(v) {
  return typeof v === 'string' && v.length > 0 && !/^https?:\/\//.test(v)
}
export function collectDraftKeys(manifest) {
  const keys = []
  ;['logo', 'bgImage'].forEach((f) => { if (isDraftKey(manifest[f])) keys.push(manifest[f]) })
  ;(manifest.pages || []).forEach((p) => { if (isDraftKey(p.img)) keys.push(p.img); if (isDraftKey(p.thumb)) keys.push(p.thumb) })
  return keys
}
